/**
 * Who owns a staged prop — the ONE predicate every surface asks.
 *
 * WHY THIS IS PROVENANCE AND NOT A NAME. Staging props are the first objects in
 * this codebase that belong to no pipeline, and roughly forty existing
 * predicates classify objects by SUBSTRING with a default-allow fallback:
 * `isFixedInPlace` matches 'counter'/'cabinet'/'shower'/..., `isWallMounted`
 * and `isGroundMounted` match 'table'/'shelf'/'sink'/'wall-art'/..., and
 * `great-room-generation/plan.js` deletes anything containing 'plant', 'rug',
 * 'table' or 'shelf'. A naming scheme that has to dodge ~39 substrings across
 * three files is behaviour by spelling luck, and the constraint list kept
 * growing every time someone read one more file.
 *
 * So the contract is a positive marker the consumers READ, and the type set is
 * only a backstop for an object whose provenance was stripped (iOS rebuilds
 * dorm objects with `attributes: {}` wholesale; assume ours can lose fields
 * the same way). The backstop is an exact-membership check against a DERIVED
 * set — never a substring.
 */

const { STAGING_PROP_DIMS } = require('./propDims');

/** The tool that emits staged decor. Stamped as `addedBy` and in `styleProvenance`. */
const STAGING_TOOL = 'style_room_decor';

/** Derived from the dimensions registry — see propDims.js. Never hand-listed. */
const STAGING_DECOR_TYPES = Object.keys(STAGING_PROP_DIMS);
const STAGING_DECOR_TYPE_SET = new Set(STAGING_DECOR_TYPES);

/**
 * Exact membership, for the predicates that only receive a type STRING
 * (`isWallMounted`, `isGroundMounted`). Not a substring test.
 */
function isStagingType(type) {
  if (!type || typeof type !== 'string') return false;
  return STAGING_DECOR_TYPE_SET.has(type.toLowerCase());
}

/**
 * The predicate. Provenance first; the derived type set is the backstop.
 * @param {Object} obj a room object
 */
/**
 * What KIND of thing a room object is, lower-cased: its `type`, or the first
 * key of its `category` for the objects that carry only that. 129 of the
 * corpus's 3,742 objects are category-only, among them toilets, tubs and
 * sinks in five bathrooms — and a planner that read `o.type` alone could not
 * see them, so nothing kept a towel out of the tub's wall section or off the
 * cistern there. One reader, for every fixture test in the planner.
 * @param {Object} obj a room object
 * @returns {string} '' when it has neither
 */
function objectTypeOf(obj) {
  if (!obj) return '';
  const raw =
    obj.type ||
    (obj.category && typeof obj.category === 'object'
      ? Object.keys(obj.category)[0]
      : obj.category) ||
    '';
  return String(raw).toLowerCase();
}

function isStagingDecor(obj) {
  if (!obj) return false;
  if (obj.addedBy === STAGING_TOOL) return true;
  if (obj.styleProvenance && obj.styleProvenance.tool === STAGING_TOOL)
    return true;
  return isStagingType(objectTypeOf(obj) || null);
}

module.exports = {
  STAGING_TOOL,
  STAGING_DECOR_TYPES,
  STAGING_DECOR_TYPE_SET,
  isStagingType,
  isStagingDecor,
  objectTypeOf,
};
